import { h, setChildren } from "../core/dom.js";
import { api } from "../api.js";
import { muscleName, profile, getBodyMap } from "../store.js";
import { navigate } from "../core/router.js";
import { icon } from "../lib/icons.js";
import { backLink, exerciseImgSrc, setSummaryText } from "../components/common.js";
import { MuscleMap } from "../components/MuscleMap.js";
import { lineChart } from "../components/Chart.js";
import { spinner, errorState } from "../components/ui.js";
import { openExportDoc } from "../components/exportModal.js";
import { exerciseToMarkdown } from "../lib/exportDoc.js";
import { fmtWeight, toDisplayWeight, weightLabel, fmtDate, epley1RM } from "../lib/format.js";
import { liftFor, strengthLevel, STRENGTH_LEVELS } from "../lib/standards.js";

export default function ExerciseDetail(params) {
  const root = h("div", { class: "screen" });
  load();

  async function load() {
    setChildren(root, backLink(), spinner());
    try {
      const [ex, pr, history] = await Promise.all([
        api.exercise(params.id),
        api.exercisePR(params.id).catch(() => null),
        api.exerciseHistory(params.id).catch(() => []),
      ]);
      render(ex, pr, history);
    } catch (e) {
      setChildren(root, backLink(), errorState(e.message, load));
    }
  }

  function render(ex, pr, history) {
    const images = (ex.images || []).map((img) => h("img", { class: "ex-detail-img", src: exerciseImgSrc(img), alt: "", loading: "lazy", style: { width: "100%", borderRadius: "12px", marginBottom: "8px" } }));

    // est 1RM trend (chronological)
    const trend = history
      .slice()
      .reverse()
      .map((s) => {
        const best = s.sets.filter((x) => !x.is_warmup).reduce((m, x) => Math.max(m, epley1RM(x.weight_kg || 0, x.reps || 0)), 0);
        return { x: s.date, y: best };
      })
      .filter((p) => p.y > 0);

    setChildren(root, 
      backLink(),
      h("h1", { style: { margin: "4px 0 4px" } }, ex.exercise_name),
      h("div", { class: "dim", style: { fontSize: ".8rem", marginBottom: "12px" } },
        ex.creator && ex.creator !== "Original" ? `Added by ${ex.creator}` : "Original library exercise"),
      h(
        "div",
        { class: "chip-row", style: { marginBottom: "14px" } },
        ...[ex.equipment, ex.level, ex.mechanic, ex.force, ex.category].filter(Boolean).map((t) => h("span", { class: "chip" }, t))
      ),

      h("div", { class: "btn-row", style: { marginBottom: "14px" } },
        canEdit(ex)
          ? h("button", { class: "btn btn-ghost grow", onClick: () => navigate(`/library/${ex.id}/edit`) }, icon("edit", { size: 18 }), "Edit")
          : null,
        h("button", { class: "btn btn-ghost grow", onClick: () => exportDoc(ex, history) }, icon("download", { size: 18 }), "Export")
      ),

      ex.video_url
        ? h("a", { href: ex.video_url, target: "_blank", rel: "noopener noreferrer",
            style: { display: "inline-flex", gap: "6px", alignItems: "center", marginBottom: "12px", color: "var(--accent)", textDecoration: "none", fontWeight: 600 } },
            icon("play", { size: 16, fill: true }), "Watch demo")
        : null,

      pr && pr.max_weight_kg != null
        ? h(
            "div",
            { class: "stat-grid mt" },
            stat(fmtWeight(pr.max_weight_kg), "Best weight"),
            stat(fmtWeight(pr.best_1rm_kg), "Est. 1RM"),
            stat(String(pr.max_reps ?? "—"), "Most reps"),
            stat(String(history.length), "Sessions")
          )
        : null,

      trendSection(ex, pr, trend),

      history.length
        ? h("div", null, h("div", { class: "section-title" }, "Your history"), ...history.map((s) => historyCard(s, ex.metric_type)))
        : h("p", { class: "dim center mt", style: { padding: "14px 0" } }, "No history yet — log this exercise in a workout."),

      h("div", { class: "section-title" }, "Target muscles"),
      h("div", { class: "card", style: { padding: "10px" } }, MuscleMap({ primary: ex.primary_muscle_ids, secondary: ex.secondary_muscle_ids }),
        h("div", { class: "chip-row", style: { marginTop: "8px", justifyContent: "center" } },
          ...(ex.primary_muscle_ids || []).map((id) => h("span", { class: "badge badge-muscle" }, muscleName(id))),
          ...(ex.secondary_muscle_ids || []).map((id) => h("span", { class: "badge badge-muscle secondary" }, muscleName(id)))
        )
      ),

      (images.length || ex.instructions?.length)
        ? h("details", { class: "card mt-lg", style: { padding: "14px 16px" } },
            h("summary", { style: { cursor: "pointer", fontWeight: 600, color: "var(--accent)" } }, "Instructions & technique photos"),
            images.length ? h("div", { style: { marginTop: "12px" } }, ...images) : null,
            ex.instructions?.length
              ? h("ol", { style: { paddingLeft: "20px", display: "flex", flexDirection: "column", gap: "8px", marginTop: "12px" } }, ...ex.instructions.map((i) => h("li", null, i)))
              : null
          )
        : null
    );
  }

  // The e1RM trend chart, with strength-band thresholds overlaid for recognised lifts:
  // dashed lines mark the bands near your data, and a callout says how far the next one is.
  // Unscored lifts (accessories, machines, unilaterals) just get the plain chart.
  function trendSection(ex, pr, trend) {
    if (trend.length < 2) return null;
    const fmt = (v) => `${toDisplayWeight(v)}${weightLabel()}`;
    const lift = liftFor(ex.exercise_name);
    const bw = profile()?.bodyweight_kg;
    const sex = getBodyMap();
    const best = pr?.best_1rm_kg || Math.max(...trend.map((p) => p.y));

    let levels = null, callout = null;
    if (lift && bw > 0 && best > 0) {
      const scale = sex === "female" ? (lift.fScale ?? 0.72) : 1;
      // Threshold i marks the entry to STRENGTH_LEVELS[i+1] (Beginner → Elite).
      const bands = lift.t.map((x, i) => ({ kg: x * scale * bw, ...STRENGTH_LEVELS[i + 1] }));
      const dataMax = Math.max(...trend.map((p) => p.y));
      const dataMin = Math.min(...trend.map((p) => p.y));
      const next = bands.find((b) => best < b.kg) || null;
      // Only overlay bands near the data — a threshold at 2× your best would squash the
      // trend flat. The next band up is always included when it's within reach of the chart.
      levels = bands
        .filter((b) => (b.kg >= dataMin * 0.75 && b.kg <= dataMax * 1.25) || (next && b === next && b.kg <= dataMax * 1.6))
        .map((b) => ({ y: b.kg, label: `${b.name} · ${fmt(b.kg)}`, color: b.color }));

      const cur = strengthLevel(ex.exercise_name, best / bw, sex);
      callout = h("div", { class: "hstack wrap", style: { marginTop: "10px", gap: "8px", alignItems: "center" } },
        cur ? h("span", { class: "badge", style: { background: "var(--surface-2)", color: cur.color } }, cur.level) : null,
        h("span", { class: "badge", style: { background: "var(--surface-2)", color: "var(--text-2)" } }, `${(best / bw).toFixed(2)}× BW`),
        h("span", { class: "dim", style: { fontSize: ".84rem" } },
          next ? `${fmtWeight(next.kg - best)} to ${next.name}` : "Top band — Elite for this lift")
      );
    }

    return h("div", null,
      h("div", { class: "section-title row-between" },
        h("span", null, "Estimated 1RM trend"),
        callout ? h("button", { class: "link-btn dim", style: { fontSize: ".82rem" }, onClick: () => navigate("/standards") }, "How levels work") : null),
      h("div", { class: "card" }, lineChart(trend, { format: fmt, levels }), callout)
    );
  }

  // Admins may edit any library entry; a member may edit only what they created.
  function canEdit(ex) {
    const p = profile();
    return !!p && (p.is_admin || ex.created_by === p.id);
  }

  function exportDoc(ex, history) {
    openExportDoc({
      title: ex.exercise_name,
      exerciseIds: [ex.id],
      build: ({ perf }) => exerciseToMarkdown(ex, { history: perf ? perf.get(ex.id) : null }),
    });
  }

  function stat(num, label) {
    return h("div", { class: "stat" }, h("div", { class: "num accent" }, num), h("div", { class: "lbl" }, label));
  }

  function historyCard(s, metricType = "weight_reps") {
    return h(
      "div",
      { class: "card", style: { marginBottom: "8px", padding: "12px" } },
      h("div", { class: "row-between" }, h("b", null, s.workout_name || "Workout"), h("span", { class: "dim" }, fmtDate(s.date))),
      h("div", { class: "chip-row mt" }, ...s.sets.map((set) => h("span", { class: "chip" }, setSummaryText(metricType, set))))
    );
  }

  return root;
}
